import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listNmfcCodes, upsertNmfcCode, deleteNmfcCode,
  listCommodities, upsertCommodity, deleteCommodity,
  listFreightClasses,
  getCustomerRoutingGuide, upsertCustomerRoutingGuide,
} from "@/lib/bol.functions";
import { listCustomersOptions } from "@/lib/admin-config.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Save, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/freight")({ component: FreightSettingsPage });

function FreightSettingsPage() {
  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-6xl">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Freight & BOL</h1>
        <p className="text-sm text-muted-foreground mt-1">NMFC codes, default commodities, and per-customer routing guides.</p>
      </div>
      <Tabs defaultValue="nmfc">
        <TabsList>
          <TabsTrigger value="nmfc">NMFC codes</TabsTrigger>
          <TabsTrigger value="commodities">Commodities</TabsTrigger>
          <TabsTrigger value="routing">Routing guides</TabsTrigger>
        </TabsList>
        <TabsContent value="nmfc"><NmfcTab /></TabsContent>
        <TabsContent value="commodities"><CommoditiesTab /></TabsContent>
        <TabsContent value="routing"><RoutingTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function NmfcTab() {
  const qc = useQueryClient();
  const listFn = useServerFn(listNmfcCodes);
  const saveFn = useServerFn(upsertNmfcCode);
  const delFn = useServerFn(deleteNmfcCode);
  const classesFn = useServerFn(listFreightClasses);
  const { data } = useQuery({ queryKey: ["nmfc"], queryFn: () => listFn({}) });
  const { data: classes } = useQuery({ queryKey: ["freight-classes"], queryFn: () => classesFn({}) });
  const [code, setCode] = useState(""); const [cls, setCls] = useState(""); const [desc, setDesc] = useState(""); const [hazmat, setHazmat] = useState(false);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!code) return;
    setBusy(true);
    try {
      await saveFn({ data: { code, default_class: cls || null, default_description: desc || null, hazmat } });
      setCode(""); setCls(""); setDesc(""); setHazmat(false);
      qc.invalidateQueries({ queryKey: ["nmfc"] });
      toast.success("NMFC code added");
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };
  const remove = async (id: string) => {
    if (!confirm("Delete this NMFC code?")) return;
    await delFn({ data: { id } });
    qc.invalidateQueries({ queryKey: ["nmfc"] });
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">NMFC Codes</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-2"><Label className="text-xs">Code</Label><Input value={code} onChange={(e) => setCode(e.target.value)} /></div>
          <div className="col-span-2"><Label className="text-xs">Class</Label>
            <Select value={cls} onValueChange={setCls}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {(classes?.classes ?? []).map((c: any) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-6"><Label className="text-xs">Description</Label><Input value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
          <div className="col-span-1 flex items-center gap-2 pb-2"><Checkbox checked={hazmat} onCheckedChange={(v) => setHazmat(!!v)} /><Label className="text-xs">Haz</Label></div>
          <div className="col-span-1"><Button onClick={add} disabled={busy || !code} size="sm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</Button></div>
        </div>
        <div className="border rounded">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs"><tr><th className="text-left p-2">Code</th><th className="text-left p-2">Class</th><th className="text-left p-2">Description</th><th className="p-2">Haz</th><th /></tr></thead>
            <tbody>
              {(data?.codes ?? []).map((c: any) => (
                <tr key={c.id} className="border-t">
                  <td className="p-2 font-mono">{c.code}</td>
                  <td className="p-2">{c.default_class ?? "—"}</td>
                  <td className="p-2 text-muted-foreground">{c.default_description ?? "—"}</td>
                  <td className="p-2 text-center">{c.hazmat ? "✓" : ""}</td>
                  <td className="p-2 text-right"><Button size="icon" variant="ghost" onClick={() => remove(c.id)}><Trash2 className="h-4 w-4" /></Button></td>
                </tr>
              ))}
              {(data?.codes ?? []).length === 0 && <tr><td colSpan={5} className="p-4 text-center text-muted-foreground text-xs">No NMFC codes yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function CommoditiesTab() {
  const qc = useQueryClient();
  const listFn = useServerFn(listCommodities);
  const saveFn = useServerFn(upsertCommodity);
  const delFn = useServerFn(deleteCommodity);
  const nmfcFn = useServerFn(listNmfcCodes);
  const classesFn = useServerFn(listFreightClasses);
  const { data } = useQuery({ queryKey: ["commodities"], queryFn: () => listFn({}) });
  const { data: nmfc } = useQuery({ queryKey: ["nmfc"], queryFn: () => nmfcFn({}) });
  const { data: classes } = useQuery({ queryKey: ["freight-classes"], queryFn: () => classesFn({}) });
  const [desc, setDesc] = useState(""); const [nmfcId, setNmfcId] = useState(""); const [cls, setCls] = useState(""); const [pkg, setPkg] = useState(""); const [hazmat, setHazmat] = useState(false);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!desc) return;
    setBusy(true);
    try {
      await saveFn({ data: { description: desc, default_nmfc_id: nmfcId || null, default_class: cls || null, default_packaging: pkg || null, hazmat } });
      setDesc(""); setNmfcId(""); setCls(""); setPkg(""); setHazmat(false);
      qc.invalidateQueries({ queryKey: ["commodities"] });
      toast.success("Commodity added");
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };
  const remove = async (id: string) => { if (!confirm("Delete?")) return; await delFn({ data: { id } }); qc.invalidateQueries({ queryKey: ["commodities"] }); };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Commodities</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-4"><Label className="text-xs">Description</Label><Input value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
          <div className="col-span-3"><Label className="text-xs">NMFC</Label>
            <Select value={nmfcId} onValueChange={setNmfcId}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{(nmfc?.codes ?? []).map((c: any) => <SelectItem key={c.id} value={c.id}>{c.code}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label className="text-xs">Class</Label>
            <Select value={cls} onValueChange={setCls}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{(classes?.classes ?? []).map((c: any) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label className="text-xs">Packaging</Label><Input value={pkg} onChange={(e) => setPkg(e.target.value)} placeholder="Pallet, Carton…" /></div>
          <div className="col-span-1"><Button onClick={add} disabled={busy || !desc} size="sm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</Button></div>
        </div>
        <div className="border rounded">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs"><tr><th className="text-left p-2">Description</th><th className="text-left p-2">NMFC</th><th className="text-left p-2">Class</th><th className="text-left p-2">Packaging</th><th className="p-2">Haz</th><th /></tr></thead>
            <tbody>
              {(data?.commodities ?? []).map((c: any) => (
                <tr key={c.id} className="border-t">
                  <td className="p-2">{c.description}</td>
                  <td className="p-2 font-mono text-xs">{c.nmfc_codes?.code ?? "—"}</td>
                  <td className="p-2">{c.default_class ?? "—"}</td>
                  <td className="p-2">{c.default_packaging ?? "—"}</td>
                  <td className="p-2 text-center">{c.hazmat ? "✓" : ""}</td>
                  <td className="p-2 text-right"><Button size="icon" variant="ghost" onClick={() => remove(c.id)}><Trash2 className="h-4 w-4" /></Button></td>
                </tr>
              ))}
              {(data?.commodities ?? []).length === 0 && <tr><td colSpan={6} className="p-4 text-center text-muted-foreground text-xs">No commodities yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function RoutingTab() {
  const customersFn = useServerFn(listCustomersOptions);
  const guideFn = useServerFn(getCustomerRoutingGuide);
  const saveFn = useServerFn(upsertCustomerRoutingGuide);
  const { data: customers } = useQuery({ queryKey: ["customers-options"], queryFn: () => customersFn({}) });
  const [customerId, setCustomerId] = useState<string>("");
  const { data: guideRes, refetch } = useQuery({
    queryKey: ["routing-guide", customerId],
    queryFn: () => guideFn({ data: { customerId } }),
    enabled: !!customerId,
  });
  const g = guideRes?.guide;
  const [state, setState] = useState<any>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!customerId) return;
    if (g) {
      setState({
        _loaded: g.id,
        require_bol: !!g.require_bol,
        require_palletized: !!g.require_palletized,
        required_carriers: (g.required_carriers ?? []).join(", "),
        bol_ref_regex: g.bol_ref_regex ?? "",
        require_po_on_bol: !!g.require_po_on_bol,
        require_pallet_labels: !!g.require_pallet_labels,
        require_master_carton_count: !!g.require_master_carton_count,
        notes: g.notes ?? "",
      });
    } else {
      setState({ _loaded: customerId, require_bol: false, require_palletized: false, required_carriers: "", bol_ref_regex: "", require_po_on_bol: false, require_pallet_labels: false, require_master_carton_count: false, notes: "" });
    }
  }, [customerId, g?.id]);

  const save = async () => {
    if (!customerId) return;
    setBusy(true);
    try {
      await saveFn({ data: {
        customerId,
        require_bol: state.require_bol,
        require_palletized: state.require_palletized,
        required_carriers: String(state.required_carriers ?? "").split(",").map((s: string) => s.trim()).filter(Boolean),
        bol_ref_regex: state.bol_ref_regex || null,
        require_po_on_bol: state.require_po_on_bol,
        require_pallet_labels: state.require_pallet_labels,
        require_master_carton_count: state.require_master_carton_count,
        notes: state.notes || null,
      } });
      toast.success("Routing guide saved");
      refetch();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Customer Routing Guides</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-md">
          <Label className="text-xs">Customer</Label>
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger><SelectValue placeholder="Choose a customer" /></SelectTrigger>
            <SelectContent>
              {(customers ?? []).map((c: any) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {customerId && (
          <div className="space-y-3 border-t pt-4">
            <ToggleRow label="Require BOL on all shipments" checked={!!state.require_bol} onChange={(v) => setState({ ...state, require_bol: v })} />
            <ToggleRow label="Require palletized shipments" checked={!!state.require_palletized} onChange={(v) => setState({ ...state, require_palletized: v })} />
            <ToggleRow label="Require PO number on BOL" checked={!!state.require_po_on_bol} onChange={(v) => setState({ ...state, require_po_on_bol: v })} />
            <ToggleRow label="Require pallet labels (LPNs)" checked={!!state.require_pallet_labels} onChange={(v) => setState({ ...state, require_pallet_labels: v })} />
            <ToggleRow label="Require master carton count" checked={!!state.require_master_carton_count} onChange={(v) => setState({ ...state, require_master_carton_count: v })} />
            <div><Label className="text-xs">Required carriers (comma-separated SCAC or names)</Label><Input value={state.required_carriers ?? ""} onChange={(e) => setState({ ...state, required_carriers: e.target.value })} /></div>
            <div><Label className="text-xs">BOL reference regex</Label><Input value={state.bol_ref_regex ?? ""} onChange={(e) => setState({ ...state, bol_ref_regex: e.target.value })} placeholder="^PO-\d{6}$" /></div>
            <div><Label className="text-xs">Notes</Label><Textarea value={state.notes ?? ""} onChange={(e) => setState({ ...state, notes: e.target.value })} rows={3} /></div>
            <Button onClick={save} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save routing guide</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
      <span>{label}</span>
    </label>
  );
}